import { ERowTypes } from "../enums";
import ContractModel from "../models/ContractModel";
import MeasurementModel from "../models/MeasurementModel";
import MeasurementRowModel from "../models/MeasurementRowModel";
import WBSModel from "../models/WBSModel";
import ExcelService, {
  ExcelWorksheet,
  alignments,
  borders,
  createBorderStyle,
  createBordersStyle,
  fillColors,
  fonts,
} from "./ExcelService";

import {
  IExportImage,
  IExportMeasurementExtraData,
  IExportMeasurementRowData,
  IExportMeasurementValue,
} from "components/interfaces";
import { formatPeriodDate } from "utils/DateUtils";
import { lang } from "../../i18nLang";
import { sum, valueToPercentual } from "../../utils/DecimalPrecision";

const bodyInitialPos = 10;

class ExportMeasurementXLSXService {
  private readonly fmtQuantity = "#,##0.00;[Red]-#,##0.00";
  private readonly fmtPercentual = '#,##0.00" %";[Red]-#,##0.00" %"';
  private readonly fmtValue = '"R$ "#,##0.00;[Red]-"R$ "#,##0.00';
  private contractFormula = "";
  private contractResult = 0;
  private totalsLines = 0;

  /** Cria uma pasta Excel com todas as medições do contrato
   * cada uma em uma planilha.
   */
  async createAllMeasurements(
    contract: ContractModel,
    measurement: MeasurementModel,
    data: IExportMeasurementRowData[],
    allMeasurements: {
      data: MeasurementModel;
      wbs: WBSModel<MeasurementRowModel>;
    }[],
    extra: IExportMeasurementExtraData
  ): Promise<ExcelService> {
    const workbook = await this.createWorksheetCurrentMeasurement(
      contract,
      measurement,
      data,
      extra
    );

    if (allMeasurements?.length)
      for (const measurementData of allMeasurements) {
        if (measurementData.data.uid !== measurement.uid) {
          const wbs = measurementData.wbs.getAllRows();

          await this.createWorkSheetMeasurement(
            workbook,
            contract,
            measurementData.data,
            data,
            wbs,
            extra
          );
        }
      }

    return workbook;
  }

  /** Cria uma planilha Excel com os dados da medição
   * que não seja a medição atualmente aberta.
   */
  private async createWorkSheetMeasurement(
    workbook: ExcelService,
    contract: ContractModel,
    measurement: MeasurementModel,
    data: IExportMeasurementRowData[],
    currentData: MeasurementRowModel[],
    extra: IExportMeasurementExtraData
  ) {
    const worksheet = workbook.createWorksheet(measurement.description);

    this.setFixedRowsHeight(worksheet);
    this.setFixedColumnsWidth(worksheet);

    this.setLogoHeader(worksheet, extra.companyImage, false);
    this.setMeasurementInfo(worksheet, contract, measurement, false);
    this.setBandedHeader(
      worksheet,
      measurement,
      extra.accumulatedColumnData?.label,
      false
    );

    this.setTitle(worksheet, false);
    this.setValues(worksheet, data, false, currentData);

    this.setFooter(
      worksheet,
      data.length + bodyInitialPos,
      contract,
      measurement,
      extra,
      data,
      false,
      currentData
    );
  }

  /** Cria uma planilha Excel com os dados da medição
   * atualmente aberta.
   */
  async createWorksheetCurrentMeasurement(
    contract: ContractModel,
    measurement: MeasurementModel,
    data: IExportMeasurementRowData[],
    extra: IExportMeasurementExtraData
  ): Promise<ExcelService> {
    const workbook = new ExcelService();
    const worksheet = workbook.createWorksheet(measurement.description);

    this.setFixedRowsHeight(worksheet);
    this.setFixedColumnsWidth(worksheet);

    this.setLogoHeader(worksheet, extra.companyImage, true);
    this.setMeasurementInfo(worksheet, contract, measurement);
    this.setBandedHeader(
      worksheet,
      measurement,
      extra.accumulatedColumnData?.label
    );
    this.setTitle(worksheet);

    this.setValues(worksheet, data);
    this.setFooter(
      worksheet,
      data.length + bodyInitialPos,
      contract,
      measurement,
      extra,
      data
    );

    return workbook;
  }

  /** Define a altura fixa das linhas da planilha. */
  private setFixedRowsHeight(worksheet: ExcelWorksheet) {
    const heights = [10, 15, 20, 20, 20, 15, 25, 25, 20];
    heights.forEach((height, index) => {
      worksheet.setRowHeight(index + 1, height);
    });
  }

  /** Define a largura fixa das colunas da planilha. */
  private setFixedColumnsWidth(worksheet: ExcelWorksheet) {
    const columnsData = [
      1, 10.71, 32.14, 20.71, 20.71, 20.71, 20.71, 20.71, 20.71, 20.71, 20.71,
      20.71, 20.71, 20.71, 20.71, 20.71, 20.71,
    ];
    columnsData.forEach((width, index) => {
      worksheet.setColumnWidth(index + 1, width);
    });
  }

  /** Cria o cabeçalho com logo do relatório. */
  private setLogoHeader(
    worksheet: ExcelWorksheet,
    companyImage?: IExportImage,
    current: boolean = true
  ) {
    if (current) {
      worksheet.mergeCells("B2:Q2");
      worksheet.mergeCells("B3:J5");
      worksheet.mergeCells("B6:Q6");
    } else {
      worksheet.mergeCells("B2:K2");
      worksheet.mergeCells("B3:D5");
      worksheet.mergeCells("B6:K6");
    }

    worksheet.cell("B2").format({
      border: {
        ...borders.allThick,
        bottom: createBorderStyle("thin", "FFFFFF"),
      },
    });
    worksheet.cell("B3").format({
      border: {
        ...borders.allThinWhite,
        left: createBorderStyle("thick", "000000"),
      },
    });
    worksheet.cell("B6").format({
      border: {
        ...borders.allThick,
        top: createBorderStyle("thin", "FFFFFF"),
      },
    });

    if (companyImage) {
      worksheet.addImage(companyImage, 1.5, 1.8);
    }
  }

  /** Adiciona as informações gerais do contrato e medição
   * no relatório. */
  private setMeasurementInfo(
    worksheet: ExcelWorksheet,
    contract: ContractModel,
    measurement: MeasurementModel,
    current: boolean = true
  ) {
    const data = {
      project: contract.development.name,
      contract: contract.name,
      client: contract.development.customer,
      location: contract.development.local,
      measurement: measurement.description,
      period: formatPeriodDate(measurement.initialDate, measurement.finalDate),
    };

    this.setMeasurementInfoTitles(worksheet, current);
    this.setMeasurementInfoValues(worksheet, data, current);
  }

  /** Define os títulos das informações gerais do contrato
   * e medição com suas formatações.
   */
  private setMeasurementInfoTitles(
    worksheet: ExcelWorksheet,
    current: boolean
  ) {
    const letter1 = current ? "K" : "E";
    const letter2 = current ? "N" : "H";
    const style = {
      font: fonts.InfoTitle,
      border: borders.allThinWhite,
    };

    worksheet
      .cell(`${letter1}3`)
      .set(lang(`measurementExport.header.project`), style);
    worksheet
      .cell(`${letter1}4`)
      .set(lang(`measurementExport.header.contract`), style);
    worksheet
      .cell(`${letter1}5`)
      .set(lang(`measurementExport.header.client`), style);
    worksheet
      .cell(`${letter2}3`)
      .set(lang(`measurementExport.header.location`), style);
    worksheet
      .cell(`${letter2}4`)
      .set(lang(`measurementExport.header.measurement`), style);
    worksheet
      .cell(`${letter2}5`)
      .set(lang(`measurementExport.header.period`), style);
  }

  /** Define os valores das informações gerais do contrato
   * e medição com suas formatações.
   */
  private setMeasurementInfoValues(
    worksheet: ExcelWorksheet,
    data: {
      project: string;
      contract: string;
      client: string;
      location: string;
      measurement: string;
      period: string;
    },
    current: boolean
  ) {
    const letter1 = current ? "L" : "F";
    const letter2 = current ? "O" : "I";
    const styleLeft = {
      font: fonts.Header,
      border: borders.allThinWhite,
    };
    const styleRight = {
      font: fonts.Header,
      border: {
        ...borders.allThinWhite,
        right: createBorderStyle("thick", "000000"),
      },
    };

    if (current) {
      worksheet.mergeCells("L3:M3");
      worksheet.mergeCells("L4:M4");
      worksheet.mergeCells("L5:M5");
      worksheet.mergeCells("O3:Q3");
      worksheet.mergeCells("O4:Q4");
      worksheet.mergeCells("O5:Q5");
    } else {
      worksheet.mergeCells("F3:G3");
      worksheet.mergeCells("F4:G4");
      worksheet.mergeCells("F5:G5");
      worksheet.mergeCells("I3:K3");
      worksheet.mergeCells("I4:K4");
      worksheet.mergeCells("I5:K5");
    }

    worksheet.cell(`${letter1}3`).set(data.project, styleLeft);
    worksheet.cell(`${letter1}4`).set(data.contract, styleLeft);
    worksheet.cell(`${letter1}5`).set(data.client, styleLeft);
    worksheet.cell(`${letter2}3`).set(data.location, styleRight);
    worksheet.cell(`${letter2}4`).set(data.measurement, styleRight);
    worksheet.cell(`${letter2}5`).set(data.period, styleRight);
  }

  /** Define os títulos dos grupos de colunas do relatório
   * com suas devidas formatações, e mergeando as células
   * necessárias.
   */
  private setBandedHeader(
    worksheet: ExcelWorksheet,
    measurement: MeasurementModel,
    accumulatedTitle: string = "",
    current: boolean = true
  ) {
    const description = measurement.description.toUpperCase();
    const withoutTopBorder = createBordersStyle(
      undefined,
      "thick",
      "thick",
      "thick"
    );

    const style = {
      font: fonts.Header,
      border: borders.allThick,
      alignment: alignments.MiddleAndCenter,
    };

    mergeCells();
    worksheet
      .cell("B7")
      .set(getTitle("contract"), { ...style, border: withoutTopBorder });
    worksheet.cell("I7").set(description, style);
    worksheet.cell("I8").format(style);

    if (current) {
      worksheet.cell("I8").set(accumulatedTitle.toUpperCase());
      worksheet.cell("L8").set(getTitle("actual"), style);
      worksheet.cell("O8").set(getTitle("balance"), style);
    }

    function getTitle(name: string) {
      return lang(`measurementExport.bands.${name}`).toUpperCase();
    }

    function mergeCells() {
      worksheet.mergeCells("B7:H8");

      if (current) {
        worksheet.mergeCells("I7:Q7");
        worksheet.mergeCells("I8:K8");
        worksheet.mergeCells("L8:N8");
        worksheet.mergeCells("O8:Q8");
      } else {
        worksheet.mergeCells("I7:K8");
      }
    }
  }

  /** Define os títulos de cada coluna do relatório com
   * suas formatações.
   */
  private setTitle(worksheet: ExcelWorksheet, current: boolean = true) {
    const unitCostLabel = `${lang("fields.unitCostShort")} (R$)`;
    const totalLabel = `${lang("fields.total")} (R$)`;
    const quantityLabel = lang("fields.quantity");

    const style = {
      font: fonts.Title,
      border: borders.allThin,
      alignment: alignments.MiddleAndCenter,
    };
    const totalStyle = { ...style, border: borders.thickRight };
    const quantityStyle = { ...style, border: borders.thickLeft };

    worksheet.cell("B9").set(lang("fields.code"), {
      font: fonts.Title,
      border: borders.thickLeft,
      alignment: alignments.MiddleAndLeft,
    });
    worksheet.cell("C9").set(lang("fields.description"), {
      font: fonts.Title,
      border: borders.allThin,
      alignment: alignments.MiddleAndLeft,
    });
    worksheet.cell("D9").set(quantityLabel, style);
    worksheet.cell("E9").set(lang("fields.unit"), style);
    worksheet.cell("F9").set(unitCostLabel, style);
    worksheet.cell("G9").set(totalLabel, style);
    worksheet.cell("H9").set("%", totalStyle);

    addMeasurementColumns(["I9", "J9", "K9"]);
    if (current) {
      addMeasurementColumns(["L9", "M9", "N9"]);
      addMeasurementColumns(["O9", "P9", "Q9"]);
    }

    function addMeasurementColumns([qttCell, percCell, totalCell]: string[]) {
      worksheet.cell(qttCell).set(quantityLabel, quantityStyle);
      worksheet.cell(percCell).set("%", style);
      worksheet.cell(totalCell).set(totalLabel, totalStyle);
    }
  }

  /** Define os valores de todas as linha da medição no relatório
   * com suas formatações.
   */
  private setValues(
    worksheet: ExcelWorksheet,
    data: IExportMeasurementRowData[],
    current: boolean = true,
    measurement?: MeasurementRowModel[]
  ) {
    data.forEach((row, i) => {
      const pos = i + bodyInitialPos;
      const isNegative = Number(row.currentToBilling.total) < 0;
      const isBeforeNegative = data[i + 1]
        ? Number(data[i + 1].currentToBilling.total) < 0
        : false;
      const isAfterNegative = data[i - 1]
        ? Number(data[i - 1].currentToBilling.total) < 0
        : false;
      const isStage = (row.rowType as number) === ERowTypes.STAGE;

      worksheet.setRowHeight(pos, 20);
      this.setCellsFormat(
        worksheet,
        pos,
        current,
        isStage,
        isNegative,
        isBeforeNegative,
        isAfterNegative
      );

      //Contrato
      worksheet.cell("B" + pos).set(row.structured);
      worksheet.cell("C" + pos).set(row.description);

      if (!isStage) {
        worksheet.cell("D" + pos).set(row.quantity);
        worksheet.cell("E" + pos).set(row.unit);
        worksheet.cell("F" + pos).set(row.unitCost);
      }

      worksheet.cell("G" + pos).set(row.total);
      worksheet.cell("H" + pos).set(row.percentual);

      if (current) {
        if (row.measured) {
          this.setMeasurementCells(worksheet, row, row.measured, pos, [
            "I",
            "J",
            "K",
          ]);
        }

        if (row.currentMeasurement) {
          this.setMeasurementCells(
            worksheet,
            row,
            row.currentMeasurement,
            pos,
            ["L", "M", "N"]
          );
        }

        if (row.currentToBilling) {
          this.setMeasurementCells(worksheet, row, row.currentToBilling, pos, [
            "O",
            "P",
            "Q",
          ]);
        }
      } else {
        let data: IExportMeasurementValue = {
          total: "-",
          quantity: "-",
          percentual: "-",
        };
        const currentMeasurement = measurement?.find(
          (measurementRow) => measurementRow.uid === row.uid
        );

        if (currentMeasurement) {
          data = {
            ...currentMeasurement,
            percentual: valueToPercentual(currentMeasurement.total, row.total),
          };
        }

        this.setMeasurementCells(worksheet, row, data, pos, ["I", "J", "K"]);
      }
    });
  }

  private setCellsFormat(
    worksheet: ExcelWorksheet,
    pos: number,
    current: boolean,
    isStage: boolean,
    isNegative: boolean,
    isBeforeNegative: boolean,
    isAfterNegative: boolean
  ) {
    const valuesStyle = {
      font: isStage ? fonts.TextBold : fonts.Text,
      border: borders.allThin,
      alignment: alignments.Middle,
      fill: pos % 2 === 0 ? fillColors.gray : undefined,
    };
    const totalStyle = { ...valuesStyle, numFmt: this.fmtValue };
    const quantityStyle = { ...valuesStyle, numFmt: this.fmtQuantity };
    const percentualStyle = { ...valuesStyle, numFmt: this.fmtPercentual };
    const qtdeMeasurement = { ...quantityStyle, border: borders.thickLeft };
    const totalMeasurement = { ...totalStyle, border: borders.thickRight };

    worksheet
      .cell("B" + pos)
      .format({ ...valuesStyle, border: borders.thickLeft });
    worksheet.cell("C" + pos).format(valuesStyle);
    worksheet.cell("D" + pos).format(quantityStyle);
    worksheet
      .cell("E" + pos)
      .format({ ...valuesStyle, alignment: alignments.MiddleAndCenter });
    worksheet.cell("F" + pos).format(totalStyle);
    worksheet.cell("G" + pos).format(totalStyle);
    worksheet
      .cell("H" + pos)
      .format({ ...percentualStyle, border: borders.thickRight });

    worksheet.cell("I" + pos).format(qtdeMeasurement);
    worksheet.cell("J" + pos).format(percentualStyle);
    worksheet.cell("K" + pos).format(totalMeasurement);

    if (!current) return;

    worksheet.cell("L" + pos).format(qtdeMeasurement);
    worksheet.cell("M" + pos).format(percentualStyle);
    worksheet.cell("N" + pos).format(totalMeasurement);

    worksheet.cell("O" + pos).format(qtdeMeasurement);
    worksheet.cell("P" + pos).format(percentualStyle);
    worksheet.cell("Q" + pos).format(totalMeasurement);

    if (isNegative) {
      this.formatNegativeBillingCells(worksheet, pos);
    } else if (isBeforeNegative) {
      this.formatCellsBeforeNegativeBilling(worksheet, pos);
    } else if (isAfterNegative) {
      this.formatCellsAfterNegativeBilling(worksheet, pos);
    }
  }

  private formatNegativeBillingCells(worksheet: ExcelWorksheet, pos: number) {
    worksheet.cell("O" + pos).setBorder(borders.allDottedRed);
    worksheet.cell("P" + pos).setBorder(borders.allDottedRed);
    worksheet.cell("Q" + pos).setBorder({
      top: createBorderStyle("dotted", "FF0000"),
      right: createBorderStyle("thick"),
      bottom: createBorderStyle("dotted", "FF0000"),
      left: createBorderStyle("dotted", "FF0000"),
    });
  }

  private formatCellsBeforeNegativeBilling(
    worksheet: ExcelWorksheet,
    pos: number
  ) {
    worksheet.cell("O" + pos).setBorder({
      ...borders.thickLeft,
      bottom: createBorderStyle("dotted", "FF0000"),
    });
    worksheet.cell("P" + pos).setBorder({
      ...borders.allThin,
      bottom: createBorderStyle("dotted", "FF0000"),
    });
    worksheet.cell("Q" + pos).setBorder({
      ...borders.thickRight,
      bottom: createBorderStyle("dotted", "FF0000"),
    });
  }

  private formatCellsAfterNegativeBilling(
    worksheet: ExcelWorksheet,
    pos: number
  ) {
    worksheet.cell("O" + pos).setBorder({
      ...borders.thickLeft,
      top: createBorderStyle("dotted", "FF0000"),
    });
    worksheet.cell("P" + pos).setBorder({
      ...borders.allThin,
      top: createBorderStyle("dotted", "FF0000"),
    });
    worksheet.cell("Q" + pos).setBorder({
      ...borders.thickRight,
      top: createBorderStyle("dotted", "FF0000"),
    });
  }

  private setMeasurementCells(
    worksheet: ExcelWorksheet,
    row: IExportMeasurementRowData,
    data: {
      quantity: number | string;
      percentual: number | string;
      total: number | string;
    },
    rowIndex: number,
    columns: string[]
  ) {
    const cellQuantity = columns[0] + rowIndex;
    const cellPercentage = columns[1] + rowIndex;
    const cellTotal = columns[2] + rowIndex;

    worksheet.cell(cellTotal).set(data.total);

    if (data.total === "-") {
      if (row.rowType === ERowTypes.RESOURCE) {
        worksheet.cell(cellQuantity).set(data.quantity);
      }
      worksheet.cell(cellPercentage).set(data.percentual);
      this.setMeasurementCellsToMiddleAndCenter(worksheet, rowIndex, columns);
    } else {
      if (row.rowType === ERowTypes.RESOURCE) {
        if (row.unitCost === 0) {
          worksheet.cell(cellQuantity).set(0);
        } else {
          worksheet
            .cell(cellQuantity)
            .setFormula(`${cellTotal}/F${rowIndex}`, Number(data.quantity));
        }
      }

      if (row.total === 0) {
        worksheet.cell(cellPercentage).set(0);
      } else {
        worksheet
          .cell(cellPercentage)
          .setFormula(`${cellTotal}/G${rowIndex}*100`, Number(data.percentual));
      }
    }
  }

  private setMeasurementCellsToMiddleAndCenter(
    worksheet: ExcelWorksheet,
    rowIndex: number,
    columns: string[]
  ) {
    worksheet
      .cell(columns[0] + rowIndex)
      .format({ alignment: alignments.MiddleAndCenter });
    worksheet
      .cell(columns[1] + rowIndex)
      .format({ alignment: alignments.MiddleAndCenter });
    worksheet
      .cell(columns[2] + rowIndex)
      .format({ alignment: alignments.MiddleAndCenter });
  }

  /** Define o rodapé do relatório com suas devidas
   * formatações.
   */
  private setFooter(
    worksheet: ExcelWorksheet,
    pos: number,
    contract: ContractModel,
    measurement: MeasurementModel,
    extra: IExportMeasurementExtraData,
    data: IExportMeasurementRowData[],
    current: boolean = true,
    measurementRows?: MeasurementRowModel[]
  ) {
    const measurementData = [
      {
        name: lang(`measurementExport.footer.totals`),
        total: measurement.total,
      },
      {
        name: lang(`measurementExport.footer.billingDiscount`),
        total: measurement.getBillingsTotal(),
      },
      {
        name: lang(`measurementExport.footer.invoice`),
        total: measurement.getGrossTotal(),
      },
      {
        name: lang(`measurementExport.footer.discount`),
        total: measurement.getDiscountsTotal(),
      },
      {
        name: lang(`measurementExport.footer.taxes`),
        total: measurement.getTaxesTotal(),
      },
      {
        name: lang(`measurementExport.footer.retention`),
        total: measurement.getTechnicalRetentionTotal(),
      },
      {
        name: lang(`measurementExport.footer.advance`),
        total: measurement.getAdvancesTotal(),
      },
      {
        name: lang(`measurementExport.footer.netTotal`),
        total: measurement.getNetTotal(),
      },
    ];
    this.totalsLines = measurementData.length;
    this.addRowsInFooter(worksheet, pos, measurementData, current);
    this.formatFooterBorders(worksheet, pos, current);

    const {
      measurementFormula,
      measurementResult,
      measuredFormula,
      measuredResult,
      billingFormula,
      billingResult,
    } = this.createTotalsFormulas(current, data, measurementRows);

    // Total do Contrato
    this.setFormula(
      worksheet,
      "G" + pos,
      this.contractFormula,
      this.contractResult
    );
    worksheet.cell("G" + pos).format({
      font: fonts.FooterValue,
      numFmt: this.fmtValue,
    });

    // Total Medição
    const measureKey = this.getMeasurementTotalKey(current);
    this.setFormula(
      worksheet,
      measureKey + pos,
      measurementFormula,
      measurementResult
    );

    if (current) {
      // Total Acumulado
      this.setFormula(worksheet, "K" + pos, measuredFormula, measuredResult);
      worksheet.cell("K" + pos).format({
        font: fonts.FooterValue,
        numFmt: this.fmtValue,
      });

      // Total Saldo a Medir
      this.setFormula(worksheet, "Q" + pos, billingFormula, billingResult);
      worksheet.cell("Q" + pos).format({
        font: fonts.FooterValue,
        numFmt: this.fmtValue,
      });

      this.addSignOnRightFooter(
        worksheet,
        pos,
        contract.development.customer,
        extra.companyName
      );
    }
  }

  private setFormula(
    worksheet: ExcelWorksheet,
    cellKey: string,
    formula: string,
    result: number
  ) {
    if (formula) {
      worksheet.cell(cellKey).setFormula(formula, result);
    } else {
      worksheet.cell(cellKey).set(0);
    }
  }

  private addRowsInFooter(
    worksheet: ExcelWorksheet,
    pos: number,
    rowData: {
      name: string;
      total: number;
    }[],
    current: boolean
  ) {
    let grossTotalFormula = "";

    rowData.forEach(({ name, total }, index) => {
      const rowIndex = pos + index;
      this.setFooterRowHeightAndAlignment(worksheet, rowIndex);

      worksheet.cell("C" + rowIndex).set(name, {
        font: fonts.FooterTitle,
        alignment: alignments.MiddleAndRight,
      });

      const totalKey = current ? "N" : "K";
      worksheet.cell(totalKey + rowIndex).set(total, {
        font: fonts.FooterValue,
        numFmt: this.fmtValue,
      });

      // Adiciona fórmula na nota fiscal
      if (index === 2) {
        this.setFormula(
          worksheet,
          totalKey + rowIndex,
          grossTotalFormula,
          total
        );
        // Adiciona fórmula no total líquido
      } else if (index === rowData.length - 1) {
        this.setFormula(
          worksheet,
          totalKey + rowIndex,
          grossTotalFormula,
          total
        );
      } else {
        grossTotalFormula += (index === 0 ? "" : " - ") + totalKey + rowIndex;
      }
    });
  }

  private createTotalsFormulas(
    current: boolean,
    data: IExportMeasurementRowData[],
    measurementRows?: MeasurementRowModel[]
  ) {
    let measurementFormula = "";
    let measuredFormula = "";
    let billingFormula = "";
    let measurementResult = 0;
    let measuredResult = 0;
    let billingResult = 0;

    if (current) {
      // É calculado na primeira página e reaproveitado nas próximas
      this.contractFormula = "";
      this.contractResult = 0;

      data.forEach((row, index) => {
        if (row.structured.includes(".")) return;

        const posBody = index + bodyInitialPos;
        this.contractFormula += this.contractFormula
          ? ` + G${posBody}`
          : `G${posBody}`;
        this.contractResult = sum(this.contractResult, row.total);

        const measureKey = this.getMeasurementTotalKey(current);
        const measureValue = row.currentMeasurement.total;
        if (measureValue !== "-") {
          measurementFormula += measurementFormula
            ? ` + ${measureKey}${posBody}`
            : `${measureKey}${posBody}`;
          measurementResult = sum(
            measurementResult,
            Number(row.currentMeasurement.total)
          );
        }

        if (row.currentToBilling.total !== "-") {
          billingFormula += billingFormula ? ` + Q${posBody}` : `Q${posBody}`;
          billingResult = sum(
            billingResult,
            Number(row.currentToBilling.total)
          );
        }

        if (row.measured.total !== "-") {
          measuredFormula += measuredFormula ? ` + K${posBody}` : `K${posBody}`;
          measuredResult = sum(measuredResult, Number(row.measured.total));
        }
      });
    } else {
      data?.forEach((row, index) => {
        if (!measurementRows || row.structured.includes(".")) return;

        const measureRow = measurementRows.find(
          (measurementRow) => measurementRow.uid === row.uid
        );
        if (!measureRow) return;

        const posBody = index + bodyInitialPos;
        const measureKey = this.getMeasurementTotalKey(current);
        measurementFormula += measurementFormula
          ? ` + ${measureKey}${posBody}`
          : `${measureKey}${posBody}`;
        measurementResult = sum(measurementResult, measureRow.total);
      });
    }

    return {
      measurementFormula,
      measurementResult,
      measuredFormula,
      measuredResult,
      billingFormula,
      billingResult,
    };
  }

  getMeasurementTotalKey(current: boolean) {
    return current ? "N" : "K";
  }

  private addSignOnRightFooter(
    worksheet: ExcelWorksheet,
    pos: number,
    customerName: string,
    companyName?: string
  ) {
    const lineStyle = {
      alignment: alignments.MiddleAndCenter,
    };

    if (companyName) {
      const posLine = pos + this.totalsLines + 1;
      const posName = pos + this.totalsLines + 2;

      this.setFooterRowHeightAndAlignment(worksheet, pos + this.totalsLines); // Linha em Branco
      this.setFooterRowHeightAndAlignment(worksheet, posLine);
      this.setFooterRowHeightAndAlignment(worksheet, posName);

      worksheet.cell("L" + posLine).set("_________________________", lineStyle);
      worksheet.cell("L" + posName).set(companyName, lineStyle);

      worksheet.cell("N" + posLine).set("_________________________", lineStyle);
      worksheet.cell("N" + posName).set(customerName, lineStyle);

      this.formatSignBorders(worksheet, posLine);
    }
  }

  private setFooterRowHeightAndAlignment(
    worksheet: ExcelWorksheet,
    rowIndex: number
  ) {
    worksheet.getRow(rowIndex).alignment = alignments.Middle;
    worksheet.setRowHeight(rowIndex, 26);
  }

  private formatFooterBorders(
    worksheet: ExcelWorksheet,
    pos: number,
    current: boolean
  ) {
    let letters = ["B", "C", "D", "E", "F", "G", "H", "I", "J", "K"];
    if (current) {
      letters = letters.concat(["L", "M", "N", "O", "P", "Q"]);
    }

    const finalRow = this.totalsLines + (current ? 3 : 0);
    for (const letter of letters) {
      for (let i = 0; i < finalRow; i++) {
        let border = createBordersStyle(
          "thin",
          "thin",
          "thin",
          "thin",
          "000000"
        );

        if (letter === letters[0]) {
          border.left = createBorderStyle("thick", "000000");
        }

        if (letter === letters[letters.length - 1]) {
          border.right = createBorderStyle("thick", "000000");
        }

        if (i === finalRow - 1) {
          border.bottom = createBorderStyle("thick", "000000");
        }

        worksheet.cell(letter + (pos + i)).format({ border });
      }
    }
  }

  private formatSignBorders(worksheet: ExcelWorksheet, pos: number) {
    const borderWhite = createBorderStyle("thin", "FFFFFF");

    worksheet.cell("K" + pos).format({
      border: { ...borders.allThin, right: borderWhite },
    });
    ["L", "M", "N"].forEach((letter) => {
      worksheet.cell(letter + pos).format({
        border: {
          ...borders.allThin,
          right: borderWhite,
          left: borderWhite,
        },
      });
    });
    worksheet.cell("O" + pos).format({
      border: { ...borders.allThin, left: borderWhite },
    });
  }
}

export default new ExportMeasurementXLSXService();
